import { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { DashboardLayout } from '../../components/DashboardLayout';
import { WelcomeConfetti } from '../../components/WelcomeConfetti';
import axios from 'axios';
import {
  Users, Calendar, Clock, TrendingUp, Plus, ArrowRight,
  UserPlus, ClipboardList, MessageSquare, Copy, CheckCircle2,
  CreditCard, FileText, ArrowLeftRight, Video, Bell, Trophy, Star, Crown, Medal, AlertTriangle, Shield
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../../components/ui/dialog';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { useNavigate, useLocation } from 'react-router-dom';
import { toast } from 'sonner';
import { ArrowLeft } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export function AdminDashboard() {
  const { user } = useAuth();
  const [showWelcome, setShowWelcome] = useState(false);
  const [stats, setStats] = useState(null);
  const [invitations, setInvitations] = useState([]);
  const [isInviteDialogOpen, setIsInviteDialogOpen] = useState(false);
  const [inviteForm, setInviteForm] = useState({ email: '', name: '', role: 'caregiver', client_usic: '' });
  const [isLoading, setIsLoading] = useState(false);
  const [inviteSuccess, setInviteSuccess] = useState(null);
  const [copiedLink, setCopiedLink] = useState(null);
  const [clients, setClients] = useState([]);
  const [leaderboard, setLeaderboard] = useState({ month: '', leaderboard: [] });
  const [compWidget, setCompWidget] = useState(null);
  const [deployWidget, setDeployWidget] = useState(null);
  const navigate = useNavigate();
  const location = useLocation();
  const isCeoVisiting = user?.role === 'ceo' || location.state?.fromCeo;

  useEffect(() => {
    const hasSeenWelcome = localStorage.getItem('admin_welcome_seen');
    if (!hasSeenWelcome && user?.role === 'agency_admin') {
      setShowWelcome(true);
      localStorage.setItem('admin_welcome_seen', 'true');
    }
    fetchStats();
    fetchInvitations();
    fetchLeaderboard();
    fetchClients();
    fetchComplianceWidget();
    fetchDeploymentWidget();
    trackAdminAccess();
  }, [user]);

  const fetchClients = async () => {
    try {
      const { data } = await axios.get(`${API_URL}/api/clinical/my-clients`, { withCredentials: true });
      setClients(Array.isArray(data) ? data : []);
    } catch { setClients([]); }
  };

  const fetchStats = async () => {
    try {
      const { data } = await axios.get(`${API_URL}/api/org/stats`, { withCredentials: true });
      setStats(data);
    } catch (err) {
      console.error('Failed to fetch stats:', err);
    }
  };

  const fetchInvitations = async () => {
    try {
      const { data } = await axios.get(`${API_URL}/api/invitations/list`, { withCredentials: true });
      setInvitations(data);
    } catch (err) {
      console.error('Failed to fetch invitations:', err);
    }
  };

  const fetchLeaderboard = async () => {
    try {
      const { data } = await axios.get(`${API_URL}/api/caregiver-management/leaderboard`, { withCredentials: true });
      setLeaderboard(data);
    } catch (err) {
      console.error('Failed to fetch leaderboard:', err);
    }
  };

  const fetchComplianceWidget = async () => {
    try {
      const { data } = await axios.get(`${API_URL}/api/docmgmt/admin/compliance-widget`, { withCredentials: true });
      setCompWidget(data);
    } catch { /* ignore */ }
  };

  const fetchDeploymentWidget = async () => {
    try {
      const { data } = await axios.get(`${API_URL}/api/hr-deployment/widget`, { withCredentials: true });
      setDeployWidget(data);
    } catch { /* ignore */ }
  };

  // Track admin dashboard access for subscription auto-regulation (once per session)
  const trackAdminAccess = async () => {
    const trackKey = `access_tracked_${Date.now() >> 16}`;
    if (sessionStorage.getItem('last_track') && Date.now() - parseInt(sessionStorage.getItem('last_track')) < 300000) return;
    sessionStorage.setItem('last_track', Date.now().toString());
    try {
      await axios.post(`${API_URL}/api/access/track`, {
        page: '/dashboard/admin',
        device_type: /Mobi|Android/i.test(navigator.userAgent) ? 'mobile' : 'desktop',
        browser: navigator.userAgent.match(/(Chrome|Firefox|Safari|Edge|Opera)[/\s](\d+)/)?.[0] || 'Unknown',
        os: navigator.platform || 'Unknown',
        screen_resolution: `${window.screen.width}x${window.screen.height}`,
        session_id: sessionStorage.getItem('session_id') || (() => { const id = crypto.randomUUID(); sessionStorage.setItem('session_id', id); return id; })(),
        device_id: localStorage.getItem('device_id') || (() => { const id = crypto.randomUUID(); localStorage.setItem('device_id', id); return id; })(),
      }, { withCredentials: true });
    } catch { /* silent */ }
  };

  const handleCreateInvitation = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setInviteSuccess(null);
    try {
      const { data } = await axios.post(`${API_URL}/api/invitations/create`, inviteForm, { withCredentials: true });
      setInviteSuccess(data);
      setInviteForm({ email: '', name: '', role: 'caregiver', client_usic: '' });
      fetchInvitations();
      if (data.email_warning) {
        const fullLink = data.full_link || `${window.location.origin}${data.invitation_link}`;
        toast.warning('Invitation email could not be sent', {
          description: `Please share this link manually: ${fullLink}`,
          duration: 12000,
          action: {
            label: 'Copy Link',
            onClick: () => {
              navigator.clipboard.writeText(fullLink);
              toast.success('Link copied');
            }
          }
        });
      } else {
        toast.success(`Invitation sent to ${data.email}`);
      }
    } catch (err) {
      alert(err.response?.data?.detail || 'Failed to create invitation');
    }
    setIsLoading(false);
  };

  const copyInviteLink = (link) => {
    const fullLink = `${window.location.origin}${link}`;
    navigator.clipboard.writeText(fullLink);
    setCopiedLink(link);
    setTimeout(() => setCopiedLink(null), 2000);
  };

  const statsCards = [
    { icon: Users, label: 'Total Caregivers', value: stats?.total_caregivers || 0, color: 'text-[#FF6F61]' },
    { icon: Users, label: 'Total Clients', value: stats?.total_clients || 0, color: 'text-[#A8E6CF]' },
    { icon: Calendar, label: 'Total Shifts', value: stats?.total_shifts || 0, color: 'text-[#D97757]' },
    { icon: Clock, label: 'Pending Shifts', value: stats?.pending_shifts || 0, color: 'text-muted-foreground' }
  ];

  return (
    <DashboardLayout>
      <WelcomeConfetti show={showWelcome} onComplete={() => setShowWelcome(false)} />

      <div className="space-y-8">
        {isCeoVisiting && (
          <Button
            variant="ghost"
            className="text-[#FF6F61] hover:bg-[#FF6F61]/10 gap-2"
            onClick={() => navigate('/dashboard/ceo')}
            data-testid="back-to-ceo-btn"
          >
            <ArrowLeft className="w-4 h-4" />Back to CEO Dashboard
          </Button>
        )}

        {/* Welcome Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl sm:text-3xl font-heading font-bold text-foreground" data-testid="welcome-heading">
              Welcome back, {user?.name?.split(' ')[0]}!
            </h1>
            <p className="text-muted-foreground mt-1">
              Here&apos;s what&apos;s happening with your agency today.
            </p>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <Button
              variant="outline"
              className="border-border text-foreground hover:bg-muted gap-2"
              onClick={() => navigate('/video')}
              data-testid="admin-video-btn"
            >
              <Video className="w-4 h-4 text-[#FF6F61]" />Video Call
            </Button>
            <Button
              variant="outline"
              className="border-border text-foreground hover:bg-muted"
              onClick={() => navigate('/messaging')}
              data-testid="admin-notifications-btn"
            >
              <Bell className="w-4 h-4 text-[#D97757]" />
            </Button>
            <Dialog open={isInviteDialogOpen} onOpenChange={setIsInviteDialogOpen}>
              <DialogTrigger asChild>
                <Button className="btn-primary" data-testid="invite-user-btn">
                  <UserPlus className="w-4 h-4 mr-2" />
                  Invite User
                </Button>
              </DialogTrigger>
            <DialogContent className="bg-background border-border max-w-md">
              <DialogHeader>
                <DialogTitle className="text-xl font-heading text-foreground">
                  Send Invitation
                </DialogTitle>
              </DialogHeader>
              
              {inviteSuccess ? (
                <div className="space-y-4 py-4">
                  {inviteSuccess.email_sent !== false ? (
                    <div className="p-4 rounded-lg bg-[#A8E6CF]/10 border border-[#A8E6CF]/20">
                      <div className="flex items-center gap-2 mb-2">
                        <CheckCircle2 className="w-5 h-5 text-[#A8E6CF]" />
                        <span className="text-[#A8E6CF] font-medium">Invitation Sent!</span>
                      </div>
                      <p className="text-sm text-muted-foreground">
                        An email with the invitation link has been sent to <span className="text-foreground font-medium">{inviteSuccess.email}</span>.
                      </p>
                    </div>
                  ) : (
                    <div className="p-4 rounded-lg bg-[#F59E0B]/10 border border-[#F59E0B]/20">
                      <div className="flex items-center gap-2 mb-2">
                        <AlertTriangle className="w-5 h-5 text-[#F59E0B]" />
                        <span className="text-[#F59E0B] font-medium">Invitation Created — Email Not Delivered</span>
                      </div>
                      <p className="text-sm text-muted-foreground">
                        The invitation for <span className="text-foreground font-medium">{inviteSuccess.email}</span> was created but the email could not be sent. Please copy and share the link below.
                      </p>
                    </div>
                  )}
                  <div className="text-sm text-muted-foreground space-y-1">
                    <p>Name: <span className="text-foreground">{inviteSuccess.name}</span></p>
                    <p>Role: <span className="capitalize text-foreground">{inviteSuccess.role}</span></p>
                    <p>USIC: <span className="font-mono text-[#FF6F61]">{inviteSuccess.usic}</span></p>
                    <p>Expires in: <span className="text-foreground">{inviteSuccess.expires_in_days} days</span></p>
                  </div>
                  <div className="p-3 rounded-lg bg-muted">
                    <p className="text-xs text-muted-foreground mb-2">You can also share this link manually:</p>
                    <div className="flex items-center gap-2">
                      <Input
                        readOnly
                        value={`${window.location.origin}${inviteSuccess.invitation_link}`}
                        className="bg-background border-border text-foreground text-xs"
                      />
                      <Button
                        onClick={() => copyInviteLink(inviteSuccess.invitation_link)}
                        variant="outline"
                        size="icon"
                        className="border-border hover:bg-muted flex-shrink-0"
                      >
                        {copiedLink === inviteSuccess.invitation_link ? (
                          <CheckCircle2 className="w-4 h-4 text-[#A8E6CF]" />
                        ) : (
                          <Copy className="w-4 h-4" />
                        )}
                      </Button>
                    </div>
                  </div>
                  <Button
                    onClick={() => { setInviteSuccess(null); setIsInviteDialogOpen(false); }}
                    className="w-full btn-primary mt-4"
                  >
                    Done
                  </Button>
                </div>
              ) : (
                <form onSubmit={handleCreateInvitation} className="space-y-4 py-4">
                  <div className="space-y-2">
                    <Label className="text-foreground">Name</Label>
                    <Input
                      value={inviteForm.name}
                      onChange={(e) => setInviteForm(prev => ({ ...prev, name: e.target.value }))}
                      placeholder="Full name"
                      required
                      className="bg-muted border-border text-foreground placeholder:text-muted-foreground"
                      data-testid="invite-name-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-foreground">Email</Label>
                    <Input
                      type="email"
                      value={inviteForm.email}
                      onChange={(e) => setInviteForm(prev => ({ ...prev, email: e.target.value }))}
                      placeholder="email@example.com"
                      required
                      className="bg-muted border-border text-foreground placeholder:text-muted-foreground"
                      data-testid="invite-email-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-foreground">Role</Label>
                    <Select
                      value={inviteForm.role}
                      onValueChange={(value) => setInviteForm(prev => ({ ...prev, role: value, client_usic: '' }))}
                    >
                      <SelectTrigger className="bg-muted border-border text-foreground" data-testid="invite-role-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="bg-background border-border">
                        <SelectItem value="caregiver">Caregiver</SelectItem>
                        <SelectItem value="client">Client</SelectItem>
                        <SelectItem value="family">Family Member</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  {inviteForm.role === 'family' && (
                    <div className="space-y-2">
                      <Label className="text-foreground">Linked Client <span className="text-red-400">*</span></Label>
                      <Select
                        value={inviteForm.client_usic}
                        onValueChange={(value) => setInviteForm(prev => ({ ...prev, client_usic: value }))}
                      >
                        <SelectTrigger className="bg-muted border-border text-foreground" data-testid="invite-client-usic-select">
                          <SelectValue placeholder="Select a client" />
                        </SelectTrigger>
                        <SelectContent className="bg-background border-border">
                          {clients.map(c => (
                            <SelectItem key={c.usic || c.id} value={c.usic || ''}>
                              {c.name} ({c.usic})
                            </SelectItem>
                          ))}
                          {clients.length === 0 && (
                            <SelectItem value="" disabled>No clients found</SelectItem>
                          )}
                        </SelectContent>
                      </Select>
                      <p className="text-[10px] text-muted-foreground">This family member will be linked to the selected client</p>
                    </div>
                  )}
                  <Button type="submit" disabled={isLoading || (inviteForm.role === 'family' && !inviteForm.client_usic)} className="w-full btn-primary mt-4" data-testid="send-invite-btn">
                    {isLoading ? 'Creating...' : 'Send Invitation'}
                  </Button>
                </form>
              )}
            </DialogContent>
          </Dialog>
          </div>
        </div>

        {/* Stats Grid - always 4 columns on desktop */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4" data-testid="stats-grid">
          {statsCards.map((stat, index) => {
            const Icon = stat.icon;
            return (
              <Card key={index} className="glass-card border-border card-hover">
                <CardContent className="p-5">
                  <div className="flex items-start justify-between">
                    <div>
                      <p className="text-muted-foreground text-sm">{stat.label}</p>
                      <p className={`text-3xl font-heading font-bold mt-2 ${stat.color}`}>
                        {stat.value}
                      </p>
                    </div>
                    <div className="p-3 rounded-xl bg-muted">
                      <Icon className={`w-5 h-5 ${stat.color}`} strokeWidth={1.5} />
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>

        {/* Compliance Widget */}
        {compWidget && (
          <Card className="glass-card border-border cursor-pointer card-hover" onClick={() => navigate('/document-storage')} data-testid="compliance-widget">
            <CardHeader className="pb-3">
              <CardTitle className="text-base text-foreground flex items-center gap-2">
                <Shield className="w-5 h-5 text-[#8B5CF6]" />Compliance Overview
                {compWidget.pending_reviews > 0 && (
                  <span className="ml-auto text-[10px] px-2 py-0.5 rounded-full bg-red-500/10 text-red-400 font-medium">{compWidget.pending_reviews} pending review</span>
                )}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-4 gap-3">
                <div className="text-center">
                  <p className={`text-xl font-bold ${compWidget.compliance_rate >= 80 ? 'text-[#A8E6CF]' : compWidget.compliance_rate >= 50 ? 'text-amber-400' : 'text-red-400'}`}>{compWidget.compliance_rate}%</p>
                  <p className="text-[10px] text-muted-foreground">Compliance</p>
                </div>
                <div className="text-center">
                  <p className="text-xl font-bold text-foreground">{compWidget.total_signoffs}/{compWidget.expected_signoffs}</p>
                  <p className="text-[10px] text-muted-foreground">Sign-offs</p>
                </div>
                <div className="text-center">
                  <p className="text-xl font-bold text-amber-400">{compWidget.pending_reviews}</p>
                  <p className="text-[10px] text-muted-foreground">Reviews</p>
                </div>
                <div className="text-center">
                  <p className="text-xl font-bold text-[#FF6F61]">{compWidget.expiring_soon}</p>
                  <p className="text-[10px] text-muted-foreground">Expiring</p>
                </div>
              </div>
              {/* Compliance bar */}
              <div className="mt-3 h-2 rounded-full bg-muted overflow-hidden">
                <div className={`h-full rounded-full transition-all ${compWidget.compliance_rate >= 80 ? 'bg-[#A8E6CF]' : compWidget.compliance_rate >= 50 ? 'bg-amber-400' : 'bg-red-400'}`}
                  style={{ width: `${compWidget.compliance_rate}%` }} />
              </div>
            </CardContent>
          </Card>
        )}

        {/* HR Deployment Gate Widget */}
        {deployWidget?.active && (
          <Card className="glass-card border-border cursor-pointer card-hover" onClick={() => navigate('/hr-compliance')} data-testid="deployment-widget">
            <CardHeader className="pb-3">
              <CardTitle className="text-base text-foreground flex items-center gap-2">
                <Shield className="w-5 h-5 text-[#00A6A0]" />HR Deployment Gate
                {deployWidget.workforce.red_critical > 0 && (
                  <span className="ml-auto text-[10px] px-2 py-0.5 rounded-full bg-red-500/10 text-red-400 font-medium">{deployWidget.workforce.red_critical} blocked</span>
                )}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-4 gap-3">
                <div className="text-center">
                  <p className="text-xl font-bold text-emerald-400">{deployWidget.today.authorised + deployWidget.today.exception}</p>
                  <p className="text-[10px] text-muted-foreground">Authorised</p>
                </div>
                <div className="text-center">
                  <p className="text-xl font-bold text-red-400">{deployWidget.today.blocked}</p>
                  <p className="text-[10px] text-muted-foreground">Blocked</p>
                </div>
                <div className="text-center">
                  <p className="text-xl font-bold text-[#00A6A0]">{deployWidget.workforce.deployable}/{deployWidget.workforce.total_staff}</p>
                  <p className="text-[10px] text-muted-foreground">Deployable</p>
                </div>
                <div className="text-center">
                  <p className="text-xl font-bold text-amber-400">{deployWidget.workforce.critical_expiring_30d}</p>
                  <p className="text-[10px] text-muted-foreground">Expiring 30d</p>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Quick Actions */}
        <div className="grid md:grid-cols-3 gap-4" data-testid="quick-actions-grid">
          {[
            { icon: Calendar, label: 'Manage Shifts', desc: 'Create and assign shifts', path: '/dashboard/admin/shifts', iconColor: 'text-[#FF6F61]', bgColor: 'bg-[#FF6F61]/10' },
            { icon: MessageSquare, label: 'Messages', desc: 'Communicate with team', path: '/messaging', iconColor: 'text-[#A8E6CF]', bgColor: 'bg-[#A8E6CF]/10' },
            { icon: ClipboardList, label: 'Reports', desc: 'View care reports', path: '/care-journals', iconColor: 'text-[#D97757]', bgColor: 'bg-[#D97757]/10' },
            { icon: FileText, label: 'Timesheets', desc: 'Generate & approve timesheets', path: '/timesheets', iconColor: 'text-[#A8E6CF]', bgColor: 'bg-[#A8E6CF]/10' },
            { icon: CreditCard, label: 'Subscription', desc: 'Manage billing & tier', path: '/subscription', iconColor: 'text-[#FF6F61]', bgColor: 'bg-[#FF6F61]/10' },
            { icon: TrendingUp, label: 'Analytics', desc: 'Reports & insights', path: '/analytics', iconColor: 'text-[#4A90E2]', bgColor: 'bg-[#4A90E2]/10' },
            { icon: Calendar, label: 'Availability', desc: 'Caregiver schedules', path: '/availability', iconColor: 'text-[#00A6A0]', bgColor: 'bg-[#00A6A0]/10' },
            { icon: ArrowLeftRight, label: 'Shift Swaps', desc: 'Review swap requests', path: '/shift-swaps', iconColor: 'text-amber-500', bgColor: 'bg-amber-500/10' },
            { icon: FileText, label: 'Documents', desc: 'Templates & compliance', path: '/document-templates', iconColor: 'text-[#A8E6CF]', bgColor: 'bg-[#A8E6CF]/10' },
          ].map((action) => {
            const Icon = action.icon;
            return (
              <Card
                key={action.path + action.label}
                className="glass-card border-border card-hover cursor-pointer"
                onClick={() => navigate(action.path)}
                data-testid={`quick-action-${action.label.toLowerCase().replace(/\s/g, '-')}`}
              >
                <CardContent className="p-5 flex items-center gap-4">
                  <div className={`p-3 rounded-xl ${action.bgColor}`}>
                    <Icon className={`w-5 h-5 ${action.iconColor}`} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className="text-foreground font-medium text-sm">{action.label}</h3>
                    <p className="text-xs text-muted-foreground truncate">{action.desc}</p>
                  </div>
                  <ArrowRight className="w-4 h-4 text-muted-foreground flex-shrink-0" />
                </CardContent>
              </Card>
            );
          })}
        </div>

        {/* Recent Invitations */}
        <Card className="glass-card border-border">
          <CardHeader className="border-b border-border">
            <div className="flex items-center justify-between">
              <CardTitle className="text-lg font-heading text-foreground">Recent Invitations</CardTitle>
              <Button variant="ghost" size="sm" className="text-[#FF6F61]" data-testid="view-all-invitations-btn">
                View All
              </Button>
            </div>
          </CardHeader>
          <CardContent className="p-0">
            {invitations.length === 0 ? (
              <div className="p-8 text-center">
                <UserPlus className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                <p className="text-muted-foreground">No invitations sent yet</p>
                <Button
                  onClick={() => setIsInviteDialogOpen(true)}
                  variant="outline"
                  className="mt-4 border-border hover:bg-muted"
                >
                  Send First Invitation
                </Button>
              </div>
            ) : (
              <div className="divide-y divide-border">
                {invitations.slice(0, 5).map((inv) => (
                  <div key={inv.id} className="p-4 flex items-center gap-3 hover:bg-muted/50 transition-colors">
                    <div className="w-10 h-10 rounded-full bg-muted flex items-center justify-center flex-shrink-0">
                      <span className="text-[#FF6F61] font-medium">
                        {inv.name?.charAt(0).toUpperCase()}
                      </span>
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-foreground font-medium truncate">{inv.name}</p>
                      <p className="text-sm text-muted-foreground truncate">{inv.email}</p>
                    </div>
                    <div className="flex items-center gap-2 flex-shrink-0">
                      <span className={`badge ${inv.used ? 'badge-success' : 'badge-primary'}`}>
                        {inv.used ? 'Joined' : inv.role}
                      </span>
                      {!inv.used && (
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => copyInviteLink(inv.invitation_link)}
                          className="text-muted-foreground hover:text-foreground h-8 w-8"
                        >
                          {copiedLink === inv.invitation_link ? (
                            <CheckCircle2 className="w-4 h-4 text-[#A8E6CF]" />
                          ) : (
                            <Copy className="w-4 h-4" />
                          )}
                        </Button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Organization Info */}
        <Card className="glass-card border-border">
          <CardHeader>
            <CardTitle className="text-lg font-heading text-foreground">Organization Details</CardTitle>
          </CardHeader>
          <CardContent className="grid sm:grid-cols-2 gap-4">
            <div>
              <p className="text-sm text-muted-foreground">Company CISC</p>
              <p className="text-[#FF6F61] font-mono">{user?.cisc}</p>
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Your USIC</p>
              <p className="text-foreground font-mono">{user?.usic}</p>
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Staff Number</p>
              <p className="text-foreground font-mono">{user?.staff_number}</p>
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Role</p>
              <p className="text-foreground capitalize">{user?.role?.replace('_', ' ')}</p>
            </div>
          </CardContent>
        </Card>

        {/* Leaderboard Widget */}
        <Card className="glass-card border-border" data-testid="leaderboard-widget">
          <CardHeader className="border-b border-border">
            <CardTitle className="text-lg font-heading text-foreground flex items-center gap-2">
              <Trophy className="w-5 h-5 text-[#FF6F61]" /> Top 5 Caregivers — {leaderboard.month || 'This Month'}
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            {leaderboard.leaderboard.length === 0 ? (
              <div className="p-6 text-center">
                <Trophy className="w-8 h-8 text-muted-foreground mx-auto mb-2" />
                <p className="text-sm text-muted-foreground">No reward data this month yet</p>
              </div>
            ) : (
              <div className="divide-y divide-border">
                {leaderboard.leaderboard.map((entry) => {
                  const RankIcon = entry.rank === 1 ? Crown : entry.rank <= 3 ? Medal : Star;
                  const rankColor = entry.rank === 1 ? 'text-amber-400' : entry.rank === 2 ? 'text-gray-300' : entry.rank === 3 ? 'text-orange-400' : 'text-muted-foreground';
                  return (
                    <div key={entry.caregiver_id} className="p-4 flex items-center gap-4 hover:bg-muted/30 transition-colors"
                      data-testid={`leaderboard-rank-${entry.rank}`}>
                      <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                        entry.rank === 1 ? 'bg-amber-400/10' : 'bg-muted'
                      }`}>
                        <RankIcon className={`w-4 h-4 ${rankColor}`} />
                      </div>
                      <span className="text-lg font-bold text-foreground w-6">#{entry.rank}</span>
                      <div className="w-8 h-8 rounded-full bg-[#00A6A0]/10 flex items-center justify-center overflow-hidden flex-shrink-0">
                        {entry.profile_photo_url ? (
                          <img src={entry.profile_photo_url} className="w-full h-full object-cover" alt="" />
                        ) : (
                          <Users className="w-4 h-4 text-[#00A6A0]" />
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-foreground truncate">{entry.name}</p>
                        <p className="text-xs text-muted-foreground">{entry.reward_count} rewards</p>
                      </div>
                      <div className="text-right">
                        <p className="text-lg font-bold text-[#FF6F61]">{entry.total_points}</p>
                        <p className="text-[10px] text-muted-foreground">points</p>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </DashboardLayout>
  );
}

export default AdminDashboard;
